<script setup lang="ts">
import type { ButtonProps } from '#ui/types';
import { useQueryClient } from '@tanstack/vue-query';
import type { PropType } from 'vue';

import {
    type BackendErrorEntity,
    type ErrorType,
    getApiAuctionDetailQueryKey,
    getApiDirectSaleDetailQueryKey,
    SellingMethod,
    useApiApproveAuction,
    useApiApproveDirectSale,
} from '~/apiClient';
import { AUCTION_DETAIL_QUERY_KEY_PREFIX, DIRECT_SALE_DETAIL_QUERY_KEY_PREFIX } from '~/constants/queryKeyPrefix';
import { isAuction, type SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { id } = useSdpParams();
const { isLoggedIn } = useAuthUtils();
const { tt } = useTypedI18n();
const { logError } = useLogs();
const { notifySuccess, notifyBackendError, notifyGenericError } = useNotification();
const queryClient = useQueryClient();
const { goToLoginPage } = useLoginRouteUtils();
const { hasToBeApproved } = useSdpToBeApproved(toRef(() => sale));
const { trackAdpConfirmButton, trackDsdpConfirmButton } = useTracking();

// DATA
const { mutateAsync: mutateApproveAuctionDraft, isPending: isApprovingAuctionDraft } = useApiApproveAuction();
const { mutateAsync: mutateApproveDirectSaleDraft, isPending: isApprovingDirectSaleDraft } = useApiApproveDirectSale();

const actions = computed<ButtonProps[]>(() =>
    isLoggedIn.value
        ? [
              {
                  label: tt(`sdp.${sale.sellingMethod}.draftConfirmation.action`),
                  color: 'neutral',
                  variant: 'outline',
                  size: 'lg',
                  class: 'text-primary ring-0 justify-center',
                  icon: 'lucide:check-line',
                  onClick: (): void => {
                      const success = (): void => {
                          notifySuccess({
                              title: tt(`sdp.${sale.sellingMethod}.draftConfirmation.success.title`),
                              description: tt(`sdp.${sale.sellingMethod}.draftConfirmation.success.description`),
                          });
                      };

                      const error = (apiError: ErrorType<BackendErrorEntity>): void => {
                          logError('Error confirm auction draft', { error: apiError });

                          if (!apiError.data?.error) {
                              notifyGenericError();

                              return;
                          }

                          const { error } = apiError.data;
                          notifyBackendError({ error });
                      };

                      trackConfirmClick();

                      switch (sale.sellingMethod) {
                          case SellingMethod.Auction:
                              mutateApproveAuctionDraft({ auctionId: sale.id })
                                  .then(data => {
                                      success();
                                      queryClient.setQueryData([AUCTION_DETAIL_QUERY_KEY_PREFIX, ...getApiAuctionDetailQueryKey(id)], data);
                                  })
                                  .catch(error);
                              break;
                          case SellingMethod.DirectSale:
                              mutateApproveDirectSaleDraft({ directSaleId: sale.id })
                                  .then(data => {
                                      success();
                                      queryClient.setQueryData([DIRECT_SALE_DETAIL_QUERY_KEY_PREFIX, ...getApiDirectSaleDetailQueryKey(id)], data);
                                  })
                                  .catch(error);
                              break;
                      }
                  },
                  loading: isAuction(sale) ? isApprovingAuctionDraft.value : isApprovingDirectSaleDraft.value,
              },
          ]
        : [
              {
                  label: tt(`sdp.${sale.sellingMethod}.draftConfirmation.loginAction`),
                  color: 'neutral',
                  variant: 'outline',
                  size: 'lg',
                  class: 'ring-0 justify-center',
                  icon: 'lucide:check-line',
                  onClick: goToLoginPage,
              },
          ]
);

// METHODS
const trackConfirmClick = (): void => {
    if (sale.sellingMethod === SellingMethod.Auction) trackAdpConfirmButton();
    if (sale.sellingMethod === SellingMethod.DirectSale) trackDsdpConfirmButton();
};
</script>

<template>
    <BaseToolbarAlert
        v-if="hasToBeApproved"
        :title="tt(`sdp.${sale.sellingMethod}.draftConfirmation.title`)"
        :description="tt(`sdp.${sale.sellingMethod}.draftConfirmation.description`)"
        :actions="actions"
        class="text-inverted"
        :class="{ 'bg-primary': isLoggedIn, 'bg-neutral-500': !isLoggedIn }"
        icon="lucide:view"
    />
    <BlackWeekToolbarAlert v-else />
</template>
